import React, { useEffect, useState } from 'react';
import {
    Breadcrumbs,
    Button,
    Card,
    Checkbox,
    CircularProgress,
    Input,
    Link,
    List,
    ListItem,
    Option,
    Select
} from "@mui/joy";
import Box from "@mui/joy/Box";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import Typography from "@mui/joy/Typography";
import FormLabel from "@mui/joy/FormLabel";
import DatePicker from "react-datepicker";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import GeneralApiService from "../../axios/Axios";

interface FranchiseNotifications {
    id: string;
    franchise: any;
    email_booking_created: boolean;
    sms_booking_created: boolean;
    email_lead_created: boolean;
    sms_lead_created: boolean;
    email_message_created: boolean;
    sms_message_created: boolean;
    email_lead_balance: boolean;
    sms_lead_balance: boolean;
    // Booking status notifications
    email_booking_cancelled: boolean;
    sms_booking_cancelled: boolean;
    email_booking_needs_rescheduling: boolean;
    sms_booking_needs_rescheduling: boolean;
    email_booking_time_changed: boolean;
    sms_booking_time_changed: boolean;
    email_booking_deleted: boolean;
    sms_booking_deleted: boolean;
    // WhatsApp message notifications
    email_whatsapp_message_created: boolean;
    sms_whatsapp_message_created: boolean;
    // Email message notifications
    email_email_message_created: boolean;
    sms_email_message_created: boolean;
    // Existing Booking call notifications
    email_existing_booking_voicemail: boolean;
    sms_existing_booking_voicemail: boolean;
    email_existing_booking_missed_call: boolean;
    sms_existing_booking_missed_call: boolean;
    // Top-up and auto-recharge notifications
    email_topup_successful: boolean;
    sms_topup_successful: boolean;
    email_auto_recharge_failed: boolean;
    sms_auto_recharge_failed: boolean;
    email_auto_recharge_monthly_cap_reached: boolean;
    sms_auto_recharge_monthly_cap_reached: boolean;
}

interface Franchise {
    notifications: any;
    location: string;
    owner: string;
    email: string;
    phone: string;
    company_name: string;
    sales_agent_active: boolean;
    arrival_after_hour: number;
}

const ARRIVAL_HOURS = [7, 8, 9, 10, 11];

export default function FranchiseSettings() {
    const [selectedDates, setSelectedDates] = useState<(string | Date)[]>([]);
    const [loadedData, setLoadingData] = useState(true);

    const [franchiseNotifications, setFranchiseNotifications] = useState<FranchiseNotifications>({
        email_booking_created: false,
        email_lead_balance: false,
        email_lead_created: false,
        email_message_created: false,
        franchise: undefined,
        sms_booking_created: false,
        sms_lead_balance: false,
        sms_lead_created: false,
        sms_message_created: false,
        id: "",
        // Booking status notifications
        email_booking_cancelled: true,
        sms_booking_cancelled: false,
        email_booking_needs_rescheduling: true,
        sms_booking_needs_rescheduling: false,
        email_booking_time_changed: true,
        sms_booking_time_changed: false,
        email_booking_deleted: true,
        sms_booking_deleted: false,
        // WhatsApp message notifications
        email_whatsapp_message_created: false,
        sms_whatsapp_message_created: false,
        // Email message notifications
        email_email_message_created: false,
        sms_email_message_created: false,
        // Existing Booking call notifications
        email_existing_booking_voicemail: false,
        sms_existing_booking_voicemail: true,
        email_existing_booking_missed_call: false,
        sms_existing_booking_missed_call: true,
        // Top-up and auto-recharge notifications
        email_topup_successful: true,
        sms_topup_successful: false,
        email_auto_recharge_failed: true,
        sms_auto_recharge_failed: false,
        email_auto_recharge_monthly_cap_reached: true,
        sms_auto_recharge_monthly_cap_reached: false,
    });
    const [franchise, setFranchise] = useState<Franchise>({
        email: "",
        owner: "",
        phone: "",
        location: "",
        company_name: "",
        sales_agent_active: true,
        arrival_after_hour: 9,
        notifications: {}
    });

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, field: keyof FranchiseNotifications) => {
        setFranchiseNotifications(prevState => ({
            ...prevState,
            [field]: event.target.checked
        }));
    };

    useEffect(() => {
        setLoadingData(true);
        const locationName = localStorage.getItem('location');
        if (!locationName) {
            toast.error("No location selected.");
            setLoadingData(false);
            return;
        }

        const getFranchise = async () => {
            try {
                const [
                    franchise_response,
                    franchise_notifications_response
                ] = await Promise.all([
                    GeneralApiService.getFranchiseByName(),
                    GeneralApiService.getFranchiseNotifications()
                ]);

                setFranchise({
                    ...franchise_response,
                    sales_agent_active: franchise_response.sales_agent_active !== false,
                    arrival_after_hour: franchise_response.arrival_after_hour ?? 9,
                });
                setFranchiseNotifications(franchise_notifications_response);

            } catch (error) {
                console.error("Failed to load franchise settings:", error);
                toast.error("Could not load franchise settings.");
            } finally {
                setLoadingData(false);
            }
        };
        getFranchise();
    }, []);

    const isWeekdayAndInPast = (date: Date) => {
        const today = new Date();
        today.setDate(today.getDate() + 3);
        today.setHours(0, 0, 0, 0);

        const targetDate = new Date(date);
        targetDate.setHours(0, 0, 0, 0);

        const day = targetDate.getDay();
        const isWeekday = day !== 0 && day !== 6;
        const isInFuture = targetDate > today;

        return isWeekday && isInFuture;
    };

    const updateFranchise = () => {
        const saveSettings = async () => {
            try {
                await GeneralApiService.postFranchiseSettings(franchise, selectedDates, franchiseNotifications);
                toast.success('Saved Franchise Settings!', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "dark",
                });
            } catch (error) {
                console.error("Failed to save franchise settings:", error);
                toast.error("Failed to save settings.");
            }
        };
        saveSettings();
    };

    const onChange = (dates: (Date | null)[]) => {
        setSelectedDates(dates.filter((date): date is Date => date !== null));
    };

    return (
        <>
            <ToastContainer />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Breadcrumbs
                    size="sm"
                    aria-label="breadcrumbs"
                    separator={<ChevronRightRoundedIcon />}
                    sx={{ pl: 0 }}
                >
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home">
                        <HomeRoundedIcon />
                    </Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>
                        Dashboard
                    </Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>
                        Franchise Settings
                    </Typography>
                </Breadcrumbs>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    mb: 1,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Typography level="h2" component="h1">
                    {franchise.location} Franchise Settings
                </Typography>
            </Box>

            {loadedData ? (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', minWidth: '50%', height: '100%', justifyContent: 'center' }}>
                    <CircularProgress size="lg" />
                </Box>
            ) : (
                <Card variant={"plain"}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                        <Box sx={{ width: { xs: '100%', md: '50%' }, flex: '48%', mb: 2 }}>
                            <Typography level="h4">
                                Information
                            </Typography>
                            <Box>
                                <FormLabel>Owner Name</FormLabel>
                                <Input onChange={(event) => setFranchise(prev => ({ ...prev, owner: event.target.value }))}
                                       sx={{ background: 'white', width: '80%' }} variant="outlined" value={franchise.owner} />
                                <FormLabel sx={{ marginTop: '5px' }}>Company Name</FormLabel>
                                <Input onChange={(event) => setFranchise(prev => ({ ...prev, company_name: event.target.value }))}
                                       sx={{ background: 'white', width: '80%' }} variant="outlined" value={franchise.company_name} placeholder="e.g. Yeti Clean Ltd" />
                                <FormLabel sx={{ marginTop: '5px' }}>Phone Number</FormLabel>
                                <Input onChange={(event) => setFranchise(prev => ({ ...prev, phone: event.target.value }))}
                                       sx={{ background: 'white', width: '80%' }} variant="outlined" value={franchise.phone} />
                                <FormLabel sx={{ marginTop: '5px' }}>Email</FormLabel>
                                <Input onChange={(event) => setFranchise(prev => ({ ...prev, email: event.target.value }))}
                                       sx={{ background: 'white', width: '80%' }} variant="outlined" value={franchise.email} />
                                <FormLabel sx={{ marginTop: '5px' }}>Arrival Time</FormLabel>
                                <Select
                                    value={franchise.arrival_after_hour}
                                    onChange={(_, value) => value && setFranchise(prev => ({ ...prev, arrival_after_hour: value }))}
                                    sx={{ background: 'white', width: '80%' }}
                                    variant="outlined"
                                >
                                    {ARRIVAL_HOURS.map(hour => (
                                        <Option key={hour} value={hour}>{hour}am</Option>
                                    ))}
                                </Select>
                                <Typography level="body-xs" color="neutral">
                                    Ronald uses this when customers ask what time you're arriving.
                                </Typography>
                                <FormLabel sx={{ marginTop: '15px' }}>Sales</FormLabel>
                                <Checkbox
                                    label="Sales Agents Handle Sales"
                                    checked={franchise.sales_agent_active}
                                    onChange={(e) => setFranchise(prev => ({ ...prev, sales_agent_active: e.target.checked }))}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ width: { xs: '100%', md: '50%' }, flex: '48%', mb: 2 }}>
                            <Typography level="h4">
                                Notifications
                            </Typography>
                            <Box sx={{ display: 'flex', width: '100%', maxWidth: '500px' }}>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>New Leads</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_lead_created} onChange={(e) => handleCheckboxChange(e, 'email_lead_created')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_lead_created} onChange={(e) => handleCheckboxChange(e, 'sms_lead_created')} /></ListItem>
                                </List>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>New Bookings</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_booking_created} onChange={(e) => handleCheckboxChange(e, 'email_booking_created')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_booking_created} onChange={(e) => handleCheckboxChange(e, 'sms_booking_created')} /></ListItem>
                                </List>
                            </Box>
                            <Box sx={{ display: 'flex', width: '100%', maxWidth: '500px' }}>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>New SMS Messages</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_message_created} onChange={(e) => handleCheckboxChange(e, 'email_message_created')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_message_created} onChange={(e) => handleCheckboxChange(e, 'sms_message_created')} /></ListItem>
                                </List>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>New WhatsApp Messages</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_whatsapp_message_created} onChange={(e) => handleCheckboxChange(e, 'email_whatsapp_message_created')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_whatsapp_message_created} onChange={(e) => handleCheckboxChange(e, 'sms_whatsapp_message_created')} /></ListItem>
                                </List>
                            </Box>
                            <Box sx={{ display: 'flex', width: '100%', maxWidth: '500px' }}>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>New Email Messages</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_email_message_created} onChange={(e) => handleCheckboxChange(e, 'email_email_message_created')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_email_message_created} onChange={(e) => handleCheckboxChange(e, 'sms_email_message_created')} /></ListItem>
                                </List>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>Lead Generation Balance Low</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_lead_balance} onChange={(e) => handleCheckboxChange(e, 'email_lead_balance')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_lead_balance} onChange={(e) => handleCheckboxChange(e, 'sms_lead_balance')} /></ListItem>
                                </List>
                            </Box>
                            
                            <Typography level="h4" sx={{ mt: 3 }}>
                                Booking Status Notifications
                            </Typography>
                            <Box sx={{ display: 'flex', width: '100%', maxWidth: '500px' }}>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>Booking Cancelled</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_booking_cancelled} onChange={(e) => handleCheckboxChange(e, 'email_booking_cancelled')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_booking_cancelled} onChange={(e) => handleCheckboxChange(e, 'sms_booking_cancelled')} /></ListItem>
                                </List>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>Booking Needs Rescheduling</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_booking_needs_rescheduling} onChange={(e) => handleCheckboxChange(e, 'email_booking_needs_rescheduling')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_booking_needs_rescheduling} onChange={(e) => handleCheckboxChange(e, 'sms_booking_needs_rescheduling')} /></ListItem>
                                </List>
                            </Box>
                            <Box sx={{ display: 'flex', width: '100%', maxWidth: '500px' }}>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>Booking Time Changed</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_booking_time_changed} onChange={(e) => handleCheckboxChange(e, 'email_booking_time_changed')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_booking_time_changed} onChange={(e) => handleCheckboxChange(e, 'sms_booking_time_changed')} /></ListItem>
                                </List>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>Booking Deleted</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_booking_deleted} onChange={(e) => handleCheckboxChange(e, 'email_booking_deleted')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_booking_deleted} onChange={(e) => handleCheckboxChange(e, 'sms_booking_deleted')} /></ListItem>
                                </List>
                            </Box>

                            <Typography level="h4" sx={{ mt: 3 }}>
                                Existing Booking Call Notifications
                            </Typography>
                            <Box sx={{ display: 'flex', width: '100%', maxWidth: '500px' }}>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>Voicemail Received</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_existing_booking_voicemail} onChange={(e) => handleCheckboxChange(e, 'email_existing_booking_voicemail')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_existing_booking_voicemail} onChange={(e) => handleCheckboxChange(e, 'sms_existing_booking_voicemail')} /></ListItem>
                                </List>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>Missed Call</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_existing_booking_missed_call} onChange={(e) => handleCheckboxChange(e, 'email_existing_booking_missed_call')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_existing_booking_missed_call} onChange={(e) => handleCheckboxChange(e, 'sms_existing_booking_missed_call')} /></ListItem>
                                </List>
                            </Box>

                            <Typography level="h4" sx={{ mt: 3 }}>
                                Top-up and auto-recharge
                            </Typography>
                            <Box sx={{ display: 'flex', width: '100%', maxWidth: '500px' }}>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>Top-up successful</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_topup_successful} onChange={(e) => handleCheckboxChange(e, 'email_topup_successful')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_topup_successful} onChange={(e) => handleCheckboxChange(e, 'sms_topup_successful')} /></ListItem>
                                </List>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>Auto-recharge failed</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_auto_recharge_failed} onChange={(e) => handleCheckboxChange(e, 'email_auto_recharge_failed')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_auto_recharge_failed} onChange={(e) => handleCheckboxChange(e, 'sms_auto_recharge_failed')} /></ListItem>
                                </List>
                            </Box>
                            <Box sx={{ display: 'flex', width: '100%', maxWidth: '500px' }}>
                                <List size="sm" sx={{ width: '50%' }}>
                                    <Typography level="body-sm" color={"neutral"}>Monthly cap reached</Typography>
                                    <ListItem><Checkbox label="Email" checked={franchiseNotifications.email_auto_recharge_monthly_cap_reached} onChange={(e) => handleCheckboxChange(e, 'email_auto_recharge_monthly_cap_reached')} /></ListItem>
                                    <ListItem><Checkbox label="Text message" checked={franchiseNotifications.sms_auto_recharge_monthly_cap_reached} onChange={(e) => handleCheckboxChange(e, 'sms_auto_recharge_monthly_cap_reached')} /></ListItem>
                                </List>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button color={"success"} onClick={updateFranchise}>
                            Save Franchise
                        </Button>
                    </Box>
                </Card>
            )}
        </>
    );
}